import { Capacitor } from '@capacitor/core';
import { isDevelopment } from './environmentUtils';

export const generateRandomString = (length: number) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const base64UrlEncode = (arrayBuffer: ArrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const sha256 = async (plain: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
};

const generateCodeVerifier = () => generateRandomString(64);

const generateCodeChallenge = async (verifier: string) => {
  const digest = await sha256(verifier);
  return base64UrlEncode(digest);
};

const exchangeAuthorizationCode = async ({
  code,
  codeVerifier,
  redirectUri,
  state,
}: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  state: string;
}) => {
  const response = await fetch('/api/auth0/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      codeVerifier,
      redirectUri,
      state,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to exchange authorization code');
  }

  if (!data.id_token) {
    throw new Error('No id_token received from Auth0 token exchange');
  }

  localStorage.setItem('bearer_token', data.id_token);

  if (data.refresh_token) {
    localStorage.setItem('auth_refresh_token', data.refresh_token);
  }

  window.location.href = '/login/callback';
};

export const redirectToAuth = async () => {
  const state = generateRandomString(32);
  const nonce = generateRandomString(32);
  
  // Store state and nonce in cookie for verification
  document.cookie = `auth_state=${state}; path=/; max-age=600; secure; samesite=strict`;
  document.cookie = `auth_nonce=${nonce}; path=/; max-age=600; secure; samesite=strict`;

  var isNative = Capacitor.isNativePlatform();
  isNative = false;

  const redirectUri = isNative
    ? 'healthscores://oauth/callback'
    : `${document.location.protocol}//${document.location.host}/callback`;
  
  if (!isNative) {
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const authUrl = `https://auth.zpaper.com/authorize?client_id=K3u6bnEsMsRA0ttovufR1DSGXtrSTi1N&scope=openid%20profile%20email&response_type=id_token&redirect_uri=${encodedRedirectUri}&response_mode=form_post&prompt=login&state=${state}&nonce=${nonce}`;
    window.location.href = authUrl;
    return;
  }
  
  try {
    // Try using Byteowls OAuth2 plugin for Auth0 login popup
    const { OAuth2Client } = await import('@byteowls/capacitor-oauth2');

    console.log('[Auth0] Starting authentication with Byteowls plugin...');

    let codeVerifier: string | null = null;
    let codeChallenge: string | null = null;

    if (isNative) {
      codeVerifier = generateCodeVerifier();
      codeChallenge = await generateCodeChallenge(codeVerifier);
      sessionStorage.setItem('auth_code_verifier', codeVerifier);
    }

    const authOptions: any = {
      appId: 'K3u6bnEsMsRA0ttovufR1DSGXtrSTi1N',
      authorizationBaseUrl: 'https://auth.zpaper.com/authorize',
      responseType: isNative ? 'code' : 'id_token',
      redirectUrl: redirectUri,
      scope: 'openid profile email',
      logsEnabled: true,
      additionalParameters: {
        response_mode: isNative ? 'query' : 'form_post',
        prompt: 'login',
        state,
        nonce,
        ...(isNative && codeChallenge
          ? {
              code_challenge: codeChallenge,
              code_challenge_method: 'S256',
            }
          : {}),
      },
    };

    authOptions.pkceEnabled = false;
    authOptions.ios = {
      presentationStyle: 'formSheet',
      prefersEphemeralWebBrowserSession: false,
    };
    authOptions.android = {
      isSystemBrowser: true,
    };

    const result = await OAuth2Client.authenticate(authOptions);

    console.log('[Auth0] OAuth result:', result);
    
    if (isNative) {
      const authorizationCode =
        result.authorization_code || result.code || result.authorizationCode;

      if (!authorizationCode) {
        throw new Error('No authorization code received from Auth0');
      }

      const storedVerifier =
        codeVerifier || sessionStorage.getItem('auth_code_verifier');

      if (!storedVerifier) {
        throw new Error('Missing PKCE code verifier for Auth0 exchange');
      }

      await exchangeAuthorizationCode({
        code: authorizationCode,
        codeVerifier: storedVerifier,
        redirectUri,
        state,
      });
      sessionStorage.removeItem('auth_code_verifier');
      return;
    }

    // Web flow: Auth0 returns id_token in the result
    if (result.id_token) {
      // Store the ID token as bearer token
      localStorage.setItem('bearer_token', result.id_token);
      // Redirect original window to unified callback handler
      window.location.href = '/login/callback';
    } else if (result.access_token) {
      // Fallback: if we got access_token, use it
      localStorage.setItem('bearer_token', result.access_token);
      window.location.href = '/login/callback';
    } else {
      throw new Error('No id_token or access_token received from Auth0');
    }
  } catch (error: any) {
    console.error('[Auth0] OAuth authentication error:', error);
    
    if (isNative) {
      throw error;
    }

    // Fallback to redirect flow on web if plugin fails
    console.log('[Auth0] Falling back to redirect flow...');
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const authUrl = `https://auth.zpaper.com/authorize?client_id=K3u6bnEsMsRA0ttovufR1DSGXtrSTi1N&scope=openid%20profile%20email&response_type=id_token&redirect_uri=${encodedRedirectUri}&response_mode=form_post&prompt=login&state=${state}&nonce=${nonce}`;
    window.location.href = authUrl;
  }
};

export const checkAuthToken = () => {
  const token = localStorage.getItem('bearer_token');
  return !!token;
};

export const requireAuth = () => {
  if (!checkAuthToken()) {
    window.location.href = '/login';
    return false;
  }
  return true;
};
